import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { resolveImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import GlobalSearch from "@/components/GlobalSearch";
import NotificationsBell from "@/components/NotificationsBell";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Newspaper, CalendarDots, UsersThree, BookOpenText, SignOut, User as UserIcon, ShieldCheck, List, X,
  Confetti, ChatTeardropDots, Moon, Sun, Trophy, CaretDown, Star, Info, Gift, MagnifyingGlass, SquaresFour, Compass, Stack,
} from "@phosphor-icons/react";

const navItems = [
  {
    label: "Mangás", icon: BookOpenText, root: "/database",
    submenu: [
      { to: "/database", label: "Base de Dados", icon: BookOpenText },
      { to: "/explorar", label: "Explorar", icon: Compass },
      { to: "/series", label: "Séries", icon: Stack },
    ],
  },
  {
    label: "Conteúdo", icon: SquaresFour, roots: ["/noticias", "/lancamentos", "/eventos", "/sorteio"],
    submenu: [
      { to: "/noticias", label: "Notícias", icon: Newspaper },
      { to: "/lancamentos", label: "Lançamentos", icon: CalendarDots },
      { to: "/eventos", label: "Eventos", icon: Confetti },
      { to: "/sorteio", label: "Sorteio", icon: Gift },
    ],
  },
  {
    label: "Comunidade", icon: UsersThree, root: "/comunidade",
    submenu: [
      { to: "/comunidade", label: "Fórum", icon: UsersThree },
      { to: "/membros", label: "Membros", icon: Star },
      { to: "/editoras", label: "Editoras", icon: BookOpenText },
    ],
  },
  {
    label: "Ranking", icon: Trophy, root: "/ranking",
    submenu: [
      { to: "/ranking", label: "Ranking", icon: Trophy },
      { to: "/ranking/como-ganhar-pontos", label: "Como Ganhar Pontos", icon: Info },
    ],
  },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="glass fixed top-0 left-0 right-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 group flex-shrink-0" data-testid="logo-link">
          <img src="/logo.png" alt="Portugal Manga" className="h-11 w-11 rounded-lg object-contain group-hover:scale-105 transition-transform" data-testid="navbar-logo" />
          <div className="leading-none hidden lg:block">
            <div className="font-display font-extrabold text-lg tracking-tight">PortugalManga</div>
            <div className="font-mono-accent text-[10px] text-zinc-500">PORTUGAL · DATABASE</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
          {navItems.map((item) => (
            item.submenu ? (
              <NavDropdown key={item.label} item={item} />
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "text-white bg-white/5" : "text-zinc-400 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <item.icon size={18} weight="bold" />
                {item.label}
              </NavLink>
            )
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <SearchMenu />
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
          {user && <NotificationsBell />}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/5 transition-colors">
                  {user.picture ? (
                    <img src={resolveImageUrl(user.picture)} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] flex items-center justify-center text-sm font-bold">
                      {(user.name || user.email)?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <span className="text-sm font-medium max-w-[140px] truncate">{user.name || user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#121217] border-zinc-800 text-white w-56">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem onClick={() => navigate("/perfil")} data-testid="menu-profile" className="cursor-pointer">
                  <UserIcon size={16} weight="bold" className="mr-2" /> Perfil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/mensagens")} data-testid="menu-messages" className="cursor-pointer">
                  <ChatTeardropDots size={16} weight="bold" className="mr-2" /> Mensagens
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin" className="cursor-pointer">
                    <ShieldCheck size={16} weight="bold" className="mr-2" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout" className="cursor-pointer">
                  <SignOut size={16} weight="bold" className="mr-2" /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" className="text-zinc-300 hover:text-white" onClick={() => navigate("/login")} data-testid="nav-login-btn">Entrar</Button>
              <Button className="btn-crimson rounded-lg" onClick={() => navigate("/registar")} data-testid="nav-register-btn">Registar</Button>
            </>
          )}
        </div>

        <div className="md:hidden flex items-center gap-2">
          {user && <NotificationsBell />}
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
          <button onClick={() => setOpen(!open)} className="p-2" data-testid="mobile-menu-btn">
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <MobileMenu
          user={user}
          logout={logout}
          navigate={navigate}
          onClose={() => setOpen(false)}
        />
      )}
    </header>
  );
}

function MobileMenu({ user, logout, navigate, onClose }) {
  const [openSection, setOpenSection] = useState(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <div className="md:hidden fixed inset-0 top-16 z-40" data-testid="mobile-menu">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute inset-x-0 top-0 max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-white/5 bg-[#09090B] px-5 py-4 space-y-1.5">
        {user && (
          <button
            onClick={() => { navigate("/perfil"); onClose(); }}
            data-testid="mobile-profile-card"
            className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 mb-1"
          >
            {user.picture ? (
              <img src={resolveImageUrl(user.picture)} alt="" className="w-11 h-11 rounded-full object-cover" />
            ) : (
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] flex items-center justify-center text-base font-bold">
                {(user.name || user.email)?.[0]?.toUpperCase()}
              </div>
            )}
            <div className="text-left min-w-0">
              <div className="text-sm font-semibold truncate">{user.name || user.email}</div>
              <div className="text-xs text-zinc-500 truncate">Ver o meu perfil</div>
            </div>
          </button>
        )}

        <div className="pb-2">
          <GlobalSearch onNavigate={onClose} />
        </div>

        {navItems.map((item) => {
          const isOpen = openSection === item.label;
          return (
            <div key={item.label} className="rounded-xl overflow-hidden bg-white/[0.02]">
              <button
                onClick={() => setOpenSection(isOpen ? null : item.label)}
                data-testid={`mobile-section-${item.label.toLowerCase()}`}
                className="w-full flex items-center gap-3 px-3 py-3 text-sm font-medium text-zinc-200"
              >
                <item.icon size={20} weight="bold" className="text-[#E63946]" />
                <span className="flex-1 text-left">{item.label}</span>
                <CaretDown size={14} weight="bold" className={`text-zinc-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="pb-2">
                  {item.submenu.map(s => (
                    <NavLink key={s.to} to={s.to} onClick={onClose} className={({ isActive }) =>
                      `flex items-center gap-3 pl-11 pr-3 py-2.5 text-sm ${isActive ? "bg-white/5 text-white" : "text-zinc-400"}`
                    }>
                      <s.icon size={16} weight="bold" /> {s.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {user && (
          <div className="pt-2 mt-1 border-t border-white/5 space-y-1">
            <NavLink to="/mensagens" onClick={onClose} data-testid="mobile-messages" className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-zinc-200 bg-white/[0.02]">
              <ChatTeardropDots size={20} weight="bold" className="text-[#E63946]" /> Mensagens
            </NavLink>
            {user.role === "admin" && (
              <NavLink to="/admin" onClick={onClose} data-testid="mobile-admin" className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold text-white bg-[#E63946]/15 border border-[#E63946]/30">
                <ShieldCheck size={20} weight="bold" className="text-[#E63946]" /> Painel de Administração
              </NavLink>
            )}
          </div>
        )}

        <div className="pt-3 flex gap-2">
          {user ? (
            <Button className="flex-1 btn-crimson" onClick={async () => { await logout(); onClose(); navigate("/"); }} data-testid="mobile-logout-btn">
              <SignOut size={16} weight="bold" className="mr-2" /> Sair
            </Button>
          ) : (
            <>
              <Button className="flex-1" variant="secondary" onClick={() => { navigate("/login"); onClose(); }} data-testid="mobile-login-btn">Entrar</Button>
              <Button className="flex-1 btn-crimson" onClick={() => { navigate("/registar"); onClose(); }} data-testid="mobile-register-btn">Registar</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SearchMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`w-10 h-10 rounded-lg grid place-items-center transition-colors ${open ? "bg-white/5 text-white" : "text-zinc-300 hover:text-white hover:bg-white/5"}`}
        data-testid="search-toggle-btn"
        aria-label="Pesquisar"
      >
        <MagnifyingGlass size={20} weight="bold" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-2 w-[360px] max-w-[90vw] z-50" data-testid="search-panel">
          <GlobalSearch autoFocus onNavigate={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}

function NavDropdown({ item }) {
  const [open, setOpen] = useState(false);
  const Icon = item.icon;
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid={`nav-dropdown-${item.label.toLowerCase()}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${open ? "text-white bg-white/5" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}
      >
        <Icon size={18} weight="bold" />
        {item.label}
        <CaretDown size={12} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 pt-1 min-w-[220px] z-50">
          <div className="bg-[#121217] border border-white/10 rounded-lg shadow-2xl overflow-hidden">
            {item.submenu.map(s => (
              <NavLink
                key={s.to}
                to={s.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-submenu-${s.to.replace(/\//g, "-").slice(1)}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${isActive ? "bg-white/5 text-white" : "text-zinc-300 hover:bg-white/5 hover:text-white"}`
                }
              >
                <s.icon size={16} weight="bold" className="text-[#E63946]" /> {s.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ThemeToggle({ theme, onToggle }) {
  const isLight = theme === "light";
  return (
    <button onClick={onToggle} data-testid="theme-toggle" aria-label={isLight ? "Ativar modo escuro" : "Ativar modo claro"} title={isLight ? "Modo escuro" : "Modo claro"} className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ${isLight ? "bg-amber-400" : "bg-zinc-800 border border-white/10"}`}>
      <span className={`absolute top-0.5 w-6 h-6 rounded-full grid place-items-center transition-all duration-300 ${isLight ? "left-[30px] bg-white text-amber-500" : "left-0.5 bg-[#09090B] text-zinc-300"}`}>
        {isLight ? <Sun size={14} weight="fill" /> : <Moon size={14} weight="fill" />}
      </span>
    </button>
  );
}
